// src/utils/dashboardConstants.js

/**
 * Period options for the dashboard filter bar.
 */
export const PERIOD_OPTIONS = [
  { value: "TODAY", label: "Today" },
  { value: "YESTERDAY", label: "Yesterday" },
  { value: "LAST_7_DAYS", label: "Last 7 Days" },
  { value: "LAST_30_DAYS", label: "Last 30 Days" },
  { value: "LAST_90_DAYS", label: "Last 90 Days" },
  { value: "THIS_WEEK", label: "This Week" },
  { value: "THIS_MONTH", label: "This Month" },
  { value: "THIS_QUARTER", label: "This Quarter" },
  { value: "CUSTOM", label: "Custom Range" },
];

/**
 * Status bucket definitions with colors for charts.
 */
export const STATUS_BUCKETS = {
  ORDER_PLACED: {
    label: "Order Placed",
    color: "#6366f1",
    bgLight: "bg-indigo-50",
    textColor: "text-indigo-600",
    borderColor: "border-indigo-200",
  },
  PICKUP_PENDING: {
    label: "Pickup Pending",
    color: "#f59e0b",
    bgLight: "bg-amber-50",
    textColor: "text-amber-600",
    borderColor: "border-amber-200",
  },
  IN_TRANSIT: {
    label: "In Transit",
    color: "#3b82f6",
    bgLight: "bg-blue-50",
    textColor: "text-blue-600",
    borderColor: "border-blue-200",
  },
  OUT_FOR_DELIVERY: {
    label: "Out for Delivery",
    color: "#8b5cf6",
    bgLight: "bg-violet-50",
    textColor: "text-violet-600",
    borderColor: "border-violet-200",
  },
  DELIVERED: {
    label: "Delivered",
    color: "#10b981",
    bgLight: "bg-emerald-50",
    textColor: "text-emerald-600",
    borderColor: "border-emerald-200",
  },
  RTO: {
    label: "RTO",
    color: "#ef4444",
    bgLight: "bg-red-50",
    textColor: "text-red-600",
    borderColor: "border-red-200",
  },
  NDR: {
    label: "NDR",
    color: "#f97316",
    bgLight: "bg-orange-50",
    textColor: "text-orange-600",
    borderColor: "border-orange-200",
  },
  CANCELLED: {
    label: "Cancelled",
    color: "#6b7280",
    bgLight: "bg-gray-50",
    textColor: "text-gray-600",
    borderColor: "border-gray-200",
  },
  LOST_DAMAGED: {
    label: "Lost / Damaged",
    color: "#dc2626",
    bgLight: "bg-red-50",
    textColor: "text-red-700",
    borderColor: "border-red-300",
  },
  OTHER: {
    label: "Other",
    color: "#9ca3af",
    bgLight: "bg-gray-50",
    textColor: "text-gray-500",
    borderColor: "border-gray-200",
  },
};

/**
 * Chart color palette — beautiful brand-aligned colors.
 */
export const CHART_COLORS = [
  "#3b82f6", // blue-500
  "#10b981", // emerald-500
  "#f59e0b", // amber-500
  "#8b5cf6", // violet-500
  "#ef4444", // red-500
  "#06b6d4", // cyan-500
  "#f97316", // orange-500
  "#ec4899", // pink-500
  "#6366f1", // indigo-500
  "#14b8a6", // teal-500
  "#a855f7", // purple-500
  "#eab308", // yellow-500
];

/**
 * Format INR currency (Indian Rupee).
 */
export const formatCurrency = (amount) => {
  if (amount === null || amount === undefined) return "₹0";
  const num = typeof amount === "string" ? parseFloat(amount) : amount;
  if (isNaN(num)) return "₹0";
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
    minimumFractionDigits: 0,
  }).format(num);
};

/**
 * Format large numbers with K/L/Cr suffixes (Indian system).
 */
export const formatNumber = (num) => {
  if (num === null || num === undefined) return "0";
  const n = typeof num === "string" ? parseFloat(num) : num;
  if (isNaN(n)) return "0";
  if (n >= 10000000) return (n / 10000000).toFixed(1).replace(/\.0$/, "") + " Cr";
  if (n >= 100000) return (n / 100000).toFixed(1).replace(/\.0$/, "") + " L";
  if (n >= 1000) return (n / 1000).toFixed(1).replace(/\.0$/, "") + "K";
  return n.toLocaleString("en-IN");
};

/**
 * Format compact number for cards.
 */
export const formatCompact = (num) => {
  if (num === null || num === undefined) return "0";
  const n = typeof num === "string" ? parseFloat(num) : num;
  if (isNaN(n)) return "0";
  return n.toLocaleString("en-IN");
};

/**
 * Format percentage.
 */
export const formatPercent = (num) => {
  if (num === null || num === undefined) return "0%";
  const n = typeof num === "string" ? parseFloat(num) : num;
  if (isNaN(n)) return "0%";
  return n.toFixed(1) + "%";
};

/**
 * Format short date for chart labels.
 */
export const formatShortDate = (dateStr) => {
  if (!dateStr) return "";
  const date = new Date(dateStr);
  if (isNaN(date)) return dateStr;
  return date.toLocaleDateString("en-IN", { day: "2-digit", month: "short" });
};

/**
 * Get change percentage between two values.
 */
export const getChangePercent = (current, previous) => {
  if (!previous || previous === 0) return current > 0 ? 100 : 0;
  return ((current - previous) / previous) * 100;
};

/**
 * Dashboard sections for tab navigation.
 */
export const DASHBOARD_SECTIONS = [
  { key: "overview", label: "Overview", icon: "LayoutDashboard" },
  { key: "shipments", label: "Shipments", icon: "Package" },
  { key: "delivery", label: "Delivery", icon: "Truck" },
  { key: "revenue", label: "Revenue", icon: "IndianRupee" },
  { key: "ndr-cod", label: "NDR & COD", icon: "AlertTriangle" },
  { key: "attempts", label: "Attempts", icon: "RotateCcw" },
];
